import React from 'react';
import {
  GridIcon,
  UserCircleIcon,
  SettingsIcon,
  FileTextIcon,
  DatabaseIcon,
  TaskIcon,
  ListIcon,
  TableIcon,
  PieChartIcon,
  BoxIcon,
  FolderIcon,
  ChevronDownIcon,
  // Add more icons as needed
} from '../../icons';

export interface DynamicIconProps {
  iconName: string;
  className?: string;
}

/**
 * Dynamic Icon Component
 * Renders icons based on string names from the backend or uploaded images
 * Supports both icon names (e.g., 'GridIcon') and image URLs/base64 strings
 */
const DynamicIcon: React.FC<DynamicIconProps> = ({ iconName, className = "w-5 h-5" }) => {
  // Check if iconName is an image (base64 or URL)
  const isImage = iconName?.startsWith('data:image/') || 
                  iconName?.startsWith('http://') || 
                  iconName?.startsWith('https://') ||
                  iconName?.startsWith('/');

  if (isImage) {
    return (
      <img 
        src={iconName} 
        alt="Menu icon" 
        className={className}
        style={{ objectFit: 'contain' }}
        onError={(e) => {
          // Fallback to default icon if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
        }}
      />
    );
  }

  // Map icon names to actual icon components
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    'GridIcon': GridIcon,
    'UserCircleIcon': UserCircleIcon,
    'SettingsIcon': SettingsIcon,
    'FileTextIcon': FileTextIcon,
    'DatabaseIcon': DatabaseIcon,
    'TaskIcon': TaskIcon,
    'ListIcon': ListIcon,
    'TableIcon': TableIcon,
    'PieChartIcon': PieChartIcon,
    'BoxIcon': BoxIcon,
    'FolderIcon': FolderIcon,
    'ChevronDownIcon': ChevronDownIcon,
  };

  const IconComponent = iconMap[iconName];

  if (!IconComponent) {
    console.warn(`Icon "${iconName}" not found, using fallback`);
    return <div className={`${className} bg-gray-400 rounded`} />;
  }

  return <IconComponent className={className} />;
};

export default DynamicIcon; 